import React from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Loader } from 'app/shared/loader/loader';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import {APP_DATE_FORMAT, CASE_TAB_ENUM} from 'app/config/constants';
import {
  fetchAccessibilityAppliedMeasureQuery,
  fetchAccessibilityRatingQuery,
  fetchBuildingIndependentFunctionalNumberQuery,
  fetchBuildingPlacementQuery, fetchBuildingPurposeByTypeQuery, fetchBuildingTypeNationalClassificationForCBPTypeQuery,
  fetchConstructionBuildingProcedureByTypeCodebookQuery,
  fetchConstructionCommencementQuery,
  fetchEnergyCertificateMarkQuery,
  fetchGreenInfrastructureTypeQuery,
  fetchRequirementGroupQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import { NumericFormat } from 'react-number-format';
import { ConstructionBuildingProcedureType } from 'app/edozvola/codebooks/model/construction-building-procedure-codebook.model';
import { queryClient } from 'app/app';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { fetchBuildingQuery, saveBuildingQuery } from '../service/construction-building-procedure.queries';
import { Building, initialBuilding } from './model/building.model';
import { buildingValidationV2Schema } from './building.schema';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import getStore from 'app/config/store';
import { revalidateTabsThunk } from 'app/shared/reducers/validationerrors/validation-errors.reducer';

export interface IBuildingFormV2Props {
  idBuilding: string | null;
  setIdBuilding: any;
  readOnly: boolean;
  variant: string;
}

const BuildingFormV2 = (props: IBuildingFormV2Props) => {
  const { id: uuid } = useParams<{ id: string }>();
  const { data: buildingData, isLoading: buildingDataLoading } = fetchBuildingQuery(props.idBuilding, props.variant);

  const { data: constructionBuildingProcedureOptions, isLoading: constructionBuildingProcedureOptionsLoading } =
    fetchConstructionBuildingProcedureByTypeCodebookQuery(uuid, ConstructionBuildingProcedureType.ZGRADA);
  const { data: buildingPurposeOptions, isLoading: buildingPurposeOptionsLoading } = fetchBuildingPurposeByTypeQuery(ConstructionBuildingProcedureType.ZGRADA);
  const { data: requirementGroupOptions, isLoading: requirementGroupOptionsLoading } = fetchRequirementGroupQuery();
  const { data: buildingPlacementOptions, isLoading: buildingPlacementOptionsLoading } = fetchBuildingPlacementQuery();
  const { data: buildingIndependentFunctionalNumberOptions, isLoading: buildingIndependentFunctionalNumberOptionsLoading } =
    fetchBuildingIndependentFunctionalNumberQuery();
  const { data: buildingTypeNationalClassificationOptions, isLoading: buildingTypeNationalClassificationOptionsLoading } =
    fetchBuildingTypeNationalClassificationForCBPTypeQuery(ConstructionBuildingProcedureType.ZGRADA);
  const { data: accessibilityRatingOptions, isLoading: accessibilityRatingOptionsLoading } = fetchAccessibilityRatingQuery();
  const { data: accessibilityAppliedMeasureOptions, isLoading: accessibilityAppliedMeasureOptionsLoading } =
    fetchAccessibilityAppliedMeasureQuery();
  const { data: greenInfrastructureTypeOptions, isLoading: greenInfrastructureTypeOptionsLoading } = fetchGreenInfrastructureTypeQuery();
  const { data: energyCertificateMark, isLoading: energyCertificateMarkLoading } = fetchEnergyCertificateMarkQuery();

  const dispatch = getStore().dispatch;

  const formik = useFormik({
    initialValues: buildingData || initialBuilding,
    validationSchema: buildingValidationV2Schema,
    onSubmit: (values: Building) => {
      saveBuildingMutation(values);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: saveBuildingMutation, isPending: isBuildingSaving } = saveBuildingQuery(uuid, props.variant, () => {
    closeModal();
  });

  const closeModal = () => {
    props.setIdBuilding(null);
    queryClient.invalidateQueries({ queryKey: ['building'] });
    dispatch(revalidateTabsThunk())
  };

  const openIspuLocator = (buildingUUid: string) => {
    const url = window.location.origin + `/ispu-locator/buildings/${buildingUUid}&${CASE_TAB_ENUM.CONSTRUCTION_BUILDING_PROCEDURE_V2}`;

    window.open(url, '_blank');
  };

  return (
    <>
      {buildingDataLoading && <Loader />}
      {!buildingDataLoading && (
        <Modal scrollable isOpen={props.idBuilding !== null} size={'xl'}>
          {props.readOnly ? (
            <ModalHeader toggle={() => closeModal()}>Pregled zgrade</ModalHeader>
          ) : (
            <ModalHeader>{props.idBuilding === '' ? 'Unos zgrade' : 'Uređivanje zgrade'}</ModalHeader>
          )}
          <ModalBody>
            <FormikProvider value={formik}>
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta zahvata'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu zahvata'}
                      onItemSelect={selected => formik.setFieldValue('constructionBuildingProcedure', selected)}
                      selected={formik.values.constructionBuildingProcedure}
                      labelKey={'value'}
                      options={constructionBuildingProcedureOptionsLoading ? [] : constructionBuildingProcedureOptions}
                      disabled={props.readOnly}
                      error={formik.errors.constructionBuildingProcedure as string}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Namjena'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi namjenu'}
                      onItemSelect={selected => formik.setFieldValue('buildingPurpose', selected)}
                      selected={formik.values.buildingPurpose}
                      labelKey={'value'}
                      options={buildingPurposeOptionsLoading ? [] : buildingPurposeOptions}
                      disabled={props.readOnly}
                      error={formik.errors.buildingPurpose as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Dodatni opis namjene'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.buildingPurposeAdditionalDescription}
                      error={
                        formik.errors.buildingPurposeAdditionalDescription ? `${formik.errors.buildingPurposeAdditionalDescription}` : ''
                      }
                      onChange={event => formik.setFieldValue('buildingPurposeAdditionalDescription', event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Skupina prema zahtjevnosti'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi skupinu prema zahtjevnosti'}
                      onItemSelect={selected => formik.setFieldValue('requirementGroup', selected)}
                      selected={formik.values.requirementGroup}
                      labelKey={'value'}
                      options={requirementGroupOptionsLoading ? [] : requirementGroupOptions}
                      disabled={props.readOnly}
                      error={formik.errors.requirementGroup as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Zgrada (*.gml)'} />
                    <div className="d-flex ispu-input-wrapper">
                      <Input
                        disabled={true}
                        placeholder={formik.values?.geometryExists ? 'Spremljena je GML datoteka.' : 'Ne postoji spremljena GML datoteka.'}
                        error={
                          !props.idBuilding
                            ? 'Potrebno je prvo spremiti zgradu prije otvaranja ISPU lokatora.'
                            : !formik.values?.geometryExists
                              ? '  '
                              : ''
                        }
                      />
                      <Button
                        disabled={!props.idBuilding}
                        label="ISPU lokator"
                        onClick={() => openIspuLocator(formik.values?.uuid)}
                        color="info"
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Identifikacijski broj zgrade (IBZ)'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.ibz}
                      error={formik.errors.ibz ? `${formik.errors.ibz}` : ''}
                      minLength={9}
                      maxLength={9}
                      onChange={event => formik.setFieldValue('ibz', event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Vanjska veličina'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.buildingExternalSize}
                      error={(formik.errors.buildingExternalSize as string) ?? ''}
                      maxLength={255}
                      onChange={event => formik.setFieldValue('buildingExternalSize', event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText="Bruto površina (GBP)" />
                    <div
                      className={
                        'd-flex align-items-center border rounded px-2' + (formik.errors.buildingGrossArea ? ' input-invalid' : '')
                      }
                    >
                      <NumericFormat
                        decimalScale={2}
                        allowNegative={false}
                        thousandSeparator="."
                        decimalSeparator=","
                        value={formik.values.buildingGrossArea || ''}
                        customInput={Input}
                        onBlur={() => formik.setFieldTouched('buildingGrossArea', true)}
                        onValueChange={values => {
                          const { value } = values;
                          formik.setFieldValue('buildingGrossArea', value);
                        }}
                        disabled={props.readOnly}
                        className={'flex-grow-1 border-0 shadow-none'}
                      />
                      <span className="ms-2 text-muted">m²</span>
                    </div>
                    {formik.errors?.buildingGrossArea && <div className="invalid-feedback d-block">{formik.errors.buildingGrossArea}</div>}
                  </Col>

                  <Col md={6}>
                    <Label labelText={'Volumen'} />
                    <div className="d-flex align-items-center border rounded px-2">
                      <NumericFormat
                        decimalScale={2}
                        allowNegative={false}
                        thousandSeparator={'.'}
                        decimalSeparator={','}
                        value={formik.values.buildingVolume || ''}
                        onError={values => {
                          const { value } = values;
                          formik.setFieldError(`buildingVolume`, value);
                        }}
                        customInput={Input}
                        onValueChange={values => {
                          const { value } = values;
                          formik.setFieldValue(`buildingVolume`, value);
                        }}
                        disabled={props.readOnly}
                        className="flex-grow-1 border-0 shadow-none"
                      />
                      <span className="ms-2 text-muted">m³</span>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Način smještaja na čestici'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi način smještaja na čestici'}
                      onItemSelect={selected => formik.setFieldValue('buildingPlacement', selected)}
                      selected={formik.values.buildingPlacement}
                      labelKey={'value'}
                      options={buildingPlacementOptionsLoading ? [] : buildingPlacementOptions}
                      disabled={props.readOnly}
                      clearButton
                      error={formik.errors.buildingPlacement as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Broj i vrsta etaža'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.buildingFloorNumberType}
                      error={(formik.errors.buildingFloorNumberType as string) ?? ''}
                      maxLength={255}
                      onChange={event => formik.setFieldValue('buildingFloorNumberType', event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Broj SUC-a'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi broj SUC-a'}
                      onItemSelect={selected => formik.setFieldValue('buildingIndependentFunctionalNumber', selected)}
                      selected={formik.values.buildingIndependentFunctionalNumber}
                      labelKey={'value'}
                      options={buildingIndependentFunctionalNumberOptionsLoading ? [] : buildingIndependentFunctionalNumberOptions}
                      disabled={props.readOnly}
                      clearButton
                      error={formik.errors.buildingIndependentFunctionalNumber as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'NKVG'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi NKVG'}
                      onItemSelect={selected => formik.setFieldValue('buildingTypeNationalClassification', selected)}
                      selected={formik.values.buildingTypeNationalClassification}
                      labelKey={'value'}
                      options={buildingTypeNationalClassificationOptionsLoading ? [] : buildingTypeNationalClassificationOptions}
                      disabled={props.readOnly}
                      clearButton
                      error={formik.errors.buildingTypeNationalClassification as string}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Pristupačnost</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Ocjena pristupačnosti'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi ocjenu pristupačnosti'}
                      onItemSelect={selected => formik.setFieldValue('accessibilityRating', selected)}
                      selected={formik.values.accessibilityRating}
                      labelKey={'value'}
                      options={accessibilityRatingOptionsLoading ? [] : accessibilityRatingOptions}
                      disabled={props.readOnly}
                      clearButton
                      error={formik.errors.accessibilityRating as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Primijenjene mjere'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi primijenjenu mjeru'}
                      onItemSelect={selected => formik.setFieldValue('accessibilityAppliedMeasure', selected)}
                      selected={formik.values.accessibilityAppliedMeasure}
                      labelKey={'value'}
                      options={accessibilityAppliedMeasureOptionsLoading ? [] : accessibilityAppliedMeasureOptions}
                      disabled={props.readOnly}
                      clearButton
                      error={formik.errors.accessibilityAppliedMeasure as string}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Zelena infrastruktura</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu'}
                      onItemSelect={selected => formik.setFieldValue('greenInfrastructureType', selected)}
                      selected={formik.values.greenInfrastructureType}
                      labelKey={'value'}
                      options={greenInfrastructureTypeOptionsLoading ? [] : greenInfrastructureTypeOptions}
                      disabled={props.readOnly}
                      clearButton
                      error={formik.errors.greenInfrastructureType as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Površina'} />
                    <div className="d-flex align-items-center border rounded px-2">
                      <NumericFormat
                        decimalScale={2}
                        allowNegative={false}
                        thousandSeparator={'.'}
                        decimalSeparator={','}
                        value={formik.values.greenInfrastructureArea || ''}
                        customInput={Input}
                        onValueChange={values => {
                          const { value } = values;
                          formik.setFieldValue(`greenInfrastructureArea`, value);
                        }}
                        disabled={props.readOnly}
                        className="flex-grow-1 border-0 shadow-none"
                      />
                      <span className="ms-2 text-muted">m²</span>
                    </div>
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Energetski certifikat</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Oznaka energetskog certifikata'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi oznaku energetskog certifikata'}
                      clearButton
                      disabled={props.readOnly}
                      options={energyCertificateMarkLoading ? [] : energyCertificateMark}
                      selected={formik.values.energyCertificateMark}
                      labelKey={'value'}
                      error={formik.errors.energyCertificateMark ? `${formik.errors.energyCertificateMark}` : ''}
                      showErrorMessage
                      onItemSelect={selected => formik.setFieldValue('energyCertificateMark', selected)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Datum izdavanja energetskog certifikata'} />
                    <Field
                      id="energyCertificateIssueDate"
                      name="energyCertificateIssueDate"
                      render={({ field }) => (
                        <FormikDatepicker
                          field={field}
                          disabled={props.readOnly}
                          setFieldValue={formik.setFieldValue}
                          dateFormat={APP_DATE_FORMAT}
                        />
                      )}
                    />
                  </Col>
                </Row>
              </div>
            </FormikProvider>
          </ModalBody>
          <ModalFooter>
            {!props.readOnly && (
              <>
                <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  disabled={!formik.dirty || !formik.isValid}
                  isLoading={isBuildingSaving}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default BuildingFormV2;
